import type { Tile } from "@/cache/SpatioTemporalTileCache/Tile";
import { Texture } from "@/cache/Texture";
import { DEFAULT_TILE_SIZE } from "@/layers/png/WMTSLayerImpl/WMTSTileManager";

export enum TileLoadState {
  NotRequested = "not_requested",
  Loading = "loading",
  Successful = "successful",
  Failed = "failed",
}

export class WmtsTile extends Texture implements Tile {
  private _loadState: TileLoadState = TileLoadState.NotRequested;
  private _isUploaded = false;

  /** Marks a cached transparent placeholder and when it should be refreshed */
  public isPlaceholder?: boolean;
  public placeholderUntil?: number;

  constructor(
    readonly x: number,
    readonly y: number,
    readonly z: number,
    readonly datetime: string,
    readonly model: string,
    readonly parameter: string,
    readonly tileSize: number = DEFAULT_TILE_SIZE,
    readonly colorMap: string = "jet",
    readonly ensSelect: string = "none",
    readonly vertical_interpolation: string | boolean = false,
    readonly calibrated: boolean = false,
    readonly initDate?: string,
    readonly apiUrlSuffix?: string,
  ) {
    super();
  }

  get isLoaded(): boolean {
    return this._loadState === TileLoadState.Successful;
  }

  get isLoading(): boolean {
    return this._loadState === TileLoadState.Loading;
  }

  get hasFailed(): boolean {
    return this._loadState === TileLoadState.Failed;
  }

  get loadState(): TileLoadState {
    return this._loadState;
  }

  get isUploaded(): boolean {
    return this._isUploaded;
  }

  toKey(): string {
    return `${this.x}/${this.y}/${this.z}/${this.datetime}/${this.model}/${this.parameter}/${this.tileSize}/${
      this.colorMap
    }/${this.ensSelect}/${this.vertical_interpolation}/${this.calibrated}/${this.initDate || ""}/${
      this.apiUrlSuffix || ""
    }`;
  }

  setLoading(): void {
    this._loadState = TileLoadState.Loading;
  }

  // Naming due to class method conflicts
  uploadImmediately(gl: WebGL2RenderingContext, imageBitmap: ImageBitmap): void {
    try {
      // Force texture creation and upload
      const texture = this.texture(gl);
      gl.bindTexture(gl.TEXTURE_2D, texture);

      const level = 0;
      gl.texImage2D(gl.TEXTURE_2D, level, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, imageBitmap);

      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

      this._isUploaded = true;
      this._loadState = TileLoadState.Successful;
    } catch (error) {
      this.setFailed();
    }
  }

  setLoaded(): void {
    this._loadState = TileLoadState.Successful;
  }

  setFailed(): void {
    this._loadState = TileLoadState.Failed;
    this._isUploaded = false;
  }

  memoryConsumptionGpu(): number {
    const BYTES_PER_CHANNEL = 4;
    return this._isUploaded ? this.tileSize * this.tileSize * BYTES_PER_CHANNEL : 0; // RGBA
  }

  memoryConsumptionCpu(): number {
    return 0;
  }

  protected upload(gl: WebGL2RenderingContext): void {
    return;
  }
}
